import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import {
  googleOauthPath,
  confirmEmailPromptPath,
} from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { hasMinLength, isValidEmail } from '$extensionSrc/utils/stringValidator'

import ContinueWithGoogle from './ContinueWithGoogle'
import FormSubmitButton from './FormSubmitButton'
import FormTextInput from './FormTextInput'
import { client } from '../apolloClient'
import {
  AddAuthenticationMethodExceptionType,
  PasswordExceptionType,
  UserExceptionType,
} from '../consts/enums'
import { addCredentialEmailPassword } from '../data/query'

import './AddAuthenticationMethodForm.scss'

function addQueryParamsRelativeUrl(
  relativeUrl: string,
  queryParams: { [key: string]: string },
) {
  const formattedQueryParams = new URLSearchParams()
  for (const [key, value] of Object.entries(queryParams)) {
    formattedQueryParams.set(key, value)
  }

  return `${relativeUrl}?${formattedQueryParams.toString()}`
}

export default function AddAuthenticationMethodForm({
  initialError,
  urlParams,
}: {
  initialError: string
  urlParams: { next?: string }
}) {
  const defaultValues = { email: '', password: '' }
  const requiredValidations = {
    email: (x: string) => isValidEmail(x),
    password: (x: string) => hasMinLength(x, 16),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const [isEmailProvided, setIsEmailProvided] = useState(false)

  const [
    submitAddEmailPasswordCredentialRequest,
    {
      data: gqlResponseData,
      error: gqlError,
      called: gqlCalled,
      loading: gqlLoading,
    },
  ] = useMutation(addCredentialEmailPassword, { client })
  const gqlServerError = gqlError?.message
  const gqlEmail = gqlResponseData?.addCredentialEmailPassword?.email

  let errorToastText
  if (gqlCalled && !gqlLoading) {
    if (!gqlServerError && !gqlError) {
      window.location.assign(
        addQueryParamsRelativeUrl(confirmEmailPromptPath, { email: gqlEmail }),
      )
    } else if (gqlServerError === UserExceptionType.USER_EXISTS_EXCEPTION) {
      errorToastText = 'User with that email already exists'
    } else if (
      gqlServerError ===
      AddAuthenticationMethodExceptionType.USER_ALREADY_HAS_ANOTHER_EMAIL_PASSWORD_EXCEPTION
    ) {
      errorToastText =
        'Users can only have one email password authentication method'
    } else if (
      gqlServerError === PasswordExceptionType.PASSWORD_TOO_SHORT_EXCEPTION
    ) {
      errorToastText = 'Password must be at least 16 characters'
    } else {
      errorToastText = 'Attempt failed'
    }
  }

  const onClickContinueWithEmail = () => {
    setIsEmailProvided(true)
  }

  const submitForm: React.FormEventHandler = (event) => {
    event.preventDefault()
    submitAddEmailPasswordCredentialRequest({
      variables: {
        email: values.email,
        password: values.password,
      },
    })
  }

  const queryParams: { action: string; next?: string } = {
    action: 'addAuthenticationMethod',
  }
  if (urlParams?.next) {
    queryParams.next = urlParams?.next
  }
  const oauthUrl: string = addQueryParamsRelativeUrl(
    googleOauthPath,
    queryParams,
  )

  return (
    <form className="AddAuthenticationMethodForm" onSubmit={submitForm}>
      <div className="w-100">
        <div className="bold heading1">Add another authentication method</div>
      </div>
      <div className="form-section">
        <div className="bold heading3">Use a new Google account:</div>
        <ContinueWithGoogle redirectUrl={oauthUrl} />
      </div>
      <div className="form-section">
        <div className="bold heading3"> Or use a new email: </div>
        <div className="form-content">
          <FormTextInput
            placeholderText="Enter your email address"
            name="email"
            isSensitive={false}
            value={values.email}
            handleChange={(value) => getFieldChangeHandler('email')(value)}
            requirements={[
              {
                text: 'Enter a valid email address',
                isFulfilled: getRequiredValidationMetByField('email'),
              },
            ]}
          />
          {isEmailProvided && (
            <FormTextInput
              placeholderText={`Create a ${BuildConfig.TENANT_NAME} password`}
              name="password"
              isSensitive
              value={values.password}
              handleChange={(value) => getFieldChangeHandler('password')(value)}
              requirements={[
                {
                  text: 'Enter 16 characters or more',
                  isFulfilled: getRequiredValidationMetByField('password'),
                },
              ]}
            />
          )}
        </div>
        {!isEmailProvided ? (
          <ActionButton
            ariaLabel="Continue with email"
            disabled={!requiredValidations.email(values.email)}
            onClick={onClickContinueWithEmail}
          >
            Continue with email
          </ActionButton>
        ) : (
          <FormSubmitButton isValid={isValid}>Confirm</FormSubmitButton>
        )}
        {errorToastText && (
          <Toast
            text={errorToastText}
            color="red"
            noAutoDismiss
            showCloseIcon
          />
        )}
        {initialError && (
          <Toast text={initialError} noAutoDismiss showCloseIcon color="red" />
        )}
      </div>
      <div className="footer">
        <div className="update-email-box body2">
          <span className="bold">Note:</span>{' '}
          <span>
            Your primary contact will also be updated to the new authentication
            method. You can change it back on the settings page.
          </span>
        </div>
      </div>
    </form>
  )
}
